import { useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Plus, Edit, Trash2, Loader2, Shield, ShieldCheck, Users } from 'lucide-react'
import { apiGet, apiDelete } from '../lib/api'

interface RoleRow {
  id: number
  name: string
  description: string
  is_system: boolean
  created_at: string | null
  permissions: number[]
  user_count: number
}

interface PermRow {
  id: number
  key: string
  label: string
  category: string
}

interface RolesResp {
  data: RoleRow[]
}
interface PermsResp {
  data: PermRow[]
}

function fmtDate(d: string | null): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return '—'
  }
}

export function Roles() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data: rolesData, isLoading } = useQuery<RolesResp>({
    queryKey: ['admin-roles'],
    queryFn: () => apiGet('/api/admin/roles'),
  })

  const { data: permsData } = useQuery<PermsResp>({
    queryKey: ['admin-permissions'],
    queryFn: () => apiGet('/api/admin/permissions'),
  })

  const totalPerms = permsData?.data.length || 0

  const deleteMut = useMutation({
    mutationFn: (id: number) => apiDelete(`/api/admin/roles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-roles'] }),
    onError: (e: Error) => alert(e.message),
  })

  function onDelete(row: RoleRow) {
    if (row.is_system) {
      alert('No se puede eliminar un rol del sistema')
      return
    }
    if (row.user_count > 0) {
      alert(`El rol tiene ${row.user_count} usuarios asignados. Quítaselos antes de eliminarlo.`)
      return
    }
    if (window.confirm(`¿Eliminar rol "${row.name}"? Esta acción no se puede deshacer.`)) {
      deleteMut.mutate(row.id)
    }
  }

  const roles = useMemo(() => rolesData?.data || [], [rolesData])

  return (
    <>
      <Topbar
        title="Roles y permisos"
        subtitle={
          rolesData
            ? `${roles.length} roles configurados · ${totalPerms} permisos en el catálogo`
            : 'Cargando…'
        }
        actions={
          <button
            type="button"
            onClick={() => navigate({ to: '/roles/nuevo' as any })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-3.5" /> Nuevo rol
          </button>
        }
      />
      <main className="p-6">
        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando roles…</p>
            </div>
          )}

          {rolesData && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3 font-medium">Rol</th>
                    <th className="px-4 py-3 font-medium">Descripción</th>
                    <th className="px-4 py-3 font-medium">Permisos</th>
                    <th className="px-4 py-3 font-medium">Usuarios</th>
                    <th className="px-4 py-3 font-medium">Creado</th>
                    <th className="w-24 px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-sm text-ink-500">
                        Sin roles configurados
                      </td>
                    </tr>
                  )}
                  {roles.map((r) => (
                    <tr key={r.id} className="border-t border-ink-100 hover:bg-ink-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {r.is_system ? (
                            <Badge variant="info">
                              <ShieldCheck className="size-3" /> sistema
                            </Badge>
                          ) : (
                            <Badge variant="default">
                              <Shield className="size-3" /> custom
                            </Badge>
                          )}
                          <span className="font-mono text-xs font-semibold text-ink-900">
                            {r.name}
                          </span>
                        </div>
                      </td>
                      <td className="max-w-md px-4 py-3 text-ink-700">
                        {r.description || (
                          <span className="text-ink-400 italic">sin descripción</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-700">
                        <span className="font-semibold">{r.permissions.length}</span>
                        <span className="text-ink-400"> / {totalPerms || '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={r.user_count > 0 ? 'success' : 'default'}>
                          <Users className="size-3" /> {r.user_count}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-500">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => navigate({ to: `/roles/${r.id}` as any })}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                            title="Editar"
                          >
                            <Edit className="size-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(r)}
                            disabled={r.is_system || deleteMut.isPending}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed"
                            title={r.is_system ? 'Rol del sistema' : 'Eliminar'}
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <p className="font-semibold">¿Cómo funcionan los roles?</p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-blue-800">
            <li>
              Los roles agrupan permisos finos (ej. <code>orders.refund</code>,{' '}
              <code>products.bulk_import</code>).
            </li>
            <li>
              Un usuario admin/operator puede tener varios roles asignados desde la pantalla{' '}
              <em>Admins</em>.
            </li>
            <li>
              Los roles <em>de sistema</em> (super_admin, operator_base) solo permiten editar sus
              permisos, no su nombre ni borrarlos.
            </li>
            <li>
              Los usuarios con <code>users.role = 'admin'</code> tienen <strong>todos</strong> los
              permisos automáticamente (bypass).
            </li>
          </ul>
        </div>
      </main>
    </>
  )
}

export default Roles
